import React, { useState, useEffect } from 'react';
// Added Grid hook for responsiveness
import { Card, Table, Progress, Tag, Typography, Button, Space, message, Grid } from 'antd';
import { FileSearchOutlined, DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import api from '../../api/axios';
import moment from 'moment';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid; // RESPONSIVE CHANGE

const AdminReports = () => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    const screens = useBreakpoint(); // RESPONSIVE CHANGE

    const fetchReports = async () => {
        setLoading(true);
        try {
            const res = await api.get('/admin/reports/detailed');
            setReports(res.data);
        } catch (e) {
            message.error("Failed to load detailed reports");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchReports(); }, []);

    const columns = [
        {
            title: 'Mentorship Pairing',
            key: 'pairing',
            // RESPONSIVE CHANGE: Fixed left on mobile to maintain context
            fixed: screens.xs ? 'left' : false,
            render: (_, record) => (
                <Space direction="vertical" size={0}>
                    <Text strong style={{ fontSize: screens.xs ? '12px' : '14px' }}>
                        {screens.xs ? `Mtr: ${record.mentor_name}` : `Mentor: ${record.mentor_name}`}
                    </Text>
                    <Text type="secondary" style={{ fontSize: screens.xs ? '11px' : '14px' }}>
                        {screens.xs ? `Mte: ${record.mentee_name}` : `Mentee: ${record.mentee_name}`}
                    </Text>
                </Space>
            )
        },
        // RESPONSIVE CHANGE: Hide dates and counts on extra small screens to save space
        // Users can scroll horizontally to see them if they exist, or we can hide them
        ...(screens.xs ? [] : [
            {
                title: 'Start Date',
                dataIndex: 'start_date',
                render: (date) => moment(date).format('ll')
            },
            {
                title: 'Goals',
                dataIndex: 'commitments_count',
                align: 'center'
            }
        ]),
        {
            title: 'Progress',
            key: 'progress',
            // RESPONSIVE CHANGE: Dynamic width
            width: screens.xs ? 120 : 250,
            render: (_, record) => (
                <div style={{ width: '100%' }}>
                    <Progress 
                        percent={record.progress} 
                        size="small" 
                        strokeColor={record.progress === 100 ? '#52c41a' : '#FE1F08'} 
                        // RESPONSIVE CHANGE: Hide percentage text on mobile to save horizontal space
                        showInfo={!screens.xs}
                    />
                    {!screens.xs && (
                        <Text style={{ fontSize: '12px' }} type="secondary">
                            {record.milestones_completed} / {record.milestones_total} Milestones
                        </Text>
                    )}
                </div>
            )
        },
        {
            title: 'Status',
            dataIndex: 'status',
            render: (status) => (
                <Tag color={status === 'active' ? 'green' : 'blue'} style={{ fontSize: '10px' }}>
                    {screens.xs ? status[0].toUpperCase() : status.toUpperCase()}
                </Tag>
            )
        }
    ];

    return (
        // RESPONSIVE CHANGE: Remove outer padding on mobile
        <div style={{ padding: screens.xs ? '8px' : '0' }}>
            <Card 
                title={<span><FileSearchOutlined /> {screens.xs ? 'Reports' : 'Performance Tracking'}</span>}
                // RESPONSIVE CHANGE: On mobile, move buttons to a separate row or hide text
                extra={
                    <Space>
                        <Button 
                            icon={<ReloadOutlined />} 
                            onClick={fetchReports} 
                            size={screens.xs ? 'small' : 'middle'}
                        />
                        <Button 
                            type="primary" 
                            icon={<DownloadOutlined />} 
                            disabled 
                            size={screens.xs ? 'small' : 'middle'}
                        >
                            {screens.xs ? '' : 'Export'}
                        </Button>
                    </Space>
                }
            >
                <Table 
                    dataSource={reports} 
                    columns={columns} 
                    rowKey="id" 
                    loading={loading}
                    pagination={{ pageSize: 10, size: 'small' }}
                    // RESPONSIVE CHANGE: Essential for wide data
                    scroll={{ x: 'max-content' }}
                />
            </Card>
        </div>
    );
};

export default AdminReports;